import Col from "@visa/vds/col";
import Row from "@visa/vds/row";
import Typography from "@visa/vds/typography";
import { FETCH_ACTION_TYPES } from "_constants/FetchActionTypes";
import { LIBRARY, LIBRARY_VISA_SECURE_TEST_TOOLS } from "_constants/PageRoutes";
import { UserRole } from "_constants/UserRole";
import { LibraryHelper } from "_helpers/LibraryHelper";
import MCSConfirmDialog from "components/MCSConfirmDialog";
import MCSGridRevert from "components/MCSGridRevert";
import MCSHeader from "components/MCSHeader";
import MCSOverlayLoading from "components/MCSOverlayLoading";
import { useAuthContextUser } from "context/AuthContext";
import { GlobalAlertContext } from "context/GlobalAlertContext";
import ErrorPage from "pages/Error/ErrorPage";
import React, { useContext, useEffect, useReducer, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { useLocation } from "react-router-dom";
import { LibraryApi } from "services/library.service";
import CategoryListSection from "./CategoryListSection";
import FilterSection from "./FilterSection";
import { LIBRARY_ACTION_TYPES, LIBRARY_INITIAL_STATE, libraryDataReducer } from "./State/libraryDataReducer";

function Library() {
    const user = useAuthContextUser();
    const alertContext = useContext(GlobalAlertContext);
    const location = useLocation();
    const { promiseInProgress } = usePromiseTracker();
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [pageState, dispatch] = useReducer(libraryDataReducer, LIBRARY_INITIAL_STATE);
    
    const handleChange = (e) => {
        let updatedTagItems = [
            ...pageState.tags.items.map((item) => ({
                ...item,
                checked: e.target.name === item.name ? !item.checked : item.checked,
            })),
        ];

        let checkedTags = LibraryHelper.getCheckedTags(updatedTagItems);

        // @ts-ignore
        dispatch({
            type: LIBRARY_ACTION_TYPES.FILTER_TAG,
            payload: {
                categories: LibraryHelper.filterCategoryByTag(checkedTags, pageState.allCategories),
                tags: {
                    items: updatedTagItems
                },
            }
        });
    };

    function fetchData(fetchFunc, opts) {
        trackPromise(
            fetchFunc()
                .then((result) => {
                    if (result.data.tags !== undefined) {
                        // @ts-ignore
                        dispatch({
                            type: FETCH_ACTION_TYPES.FETCH_SUCCESS,
                            payload: applyInitialFiltersToPayload({
                                pageTitle: opts.title,
                                allCategories: result.data.categories,
                                categories: result.data.categories,
                                tags: { items: result.data.tags },
                                hasTags: result.data.tags.length > 0,
                                defaultExpanded: opts.expanded,
                            })
                        });
                    } else {
                        // @ts-ignore
                        dispatch({
                            type: FETCH_ACTION_TYPES.FETCH_SUCCESS,
                            payload: {
                                pageTitle: opts.title,
                                allCategories: result.data.categories,
                                categories: result.data.categories,
                                tags: { items: [] },
                                hasTags: false,
                                defaultExpanded: opts.expanded,
                            }
                        });
                    }
                })
                .catch((error) => {
                    // @ts-ignore
                    dispatch({
                        type: FETCH_ACTION_TYPES.FETCH_ERROR,
                        payload: {
                            pageTitle: opts.title,
                            error: error,
                        }
                    });

                    alertContext.setGlobalErrorMessage(error.message);
                })
        );
    }

    const handleClickLinkBtn = (post, action) => {
        let data = {
            postId: post.id,
            postType: post.type,
            action: action,
        };

        if (post.notice != null) {
            LibraryHelper.openLibraryNoticeDialog(setConfirmDialog, confirmDialog, post.notice, data);
        } else {
            LibraryHelper.handleLibraryPostOnClick(data);
        }
    };

    const applyInitialFiltersToPayload = (payload) => {
        const searchParams = new URLSearchParams(location.search);
        let filters = searchParams.get('filters')?.split(",");
        return LibraryHelper.applyFiltersToPayload(payload, filters);
    }

    useEffect(() => {
        if (location.pathname === LIBRARY) {
            fetchData(LibraryApi.getCategoryList, {
                title: "VSS Library",
                expanded: false
            });
        } else if (location.pathname === LIBRARY_VISA_SECURE_TEST_TOOLS) {
            fetchData(LibraryApi.getVisaSecureTestTools, {
                title: "Implementation Testing - Visa Secure Test Tools",
                expanded: true
            });
        }
    }, [location]);

    if (pageState.error) {
        return <ErrorPage text={pageState.error.message} />;
    } else {
        return (
            <GlobalAlertContext.Consumer>
                {(alertObj) => (
                    <>
                        {promiseInProgress && <MCSOverlayLoading />}
                        <MCSGridRevert>
                            <MCSHeader header={pageState.pageTitle} />
                            {!pageState.categories && (
                                <Row>
                                    <Col>
                                        <Typography tag="h4" variant="subtitle--alt">
                                            No documents available.
                                        </Typography>
                                    </Col>
                                </Row>
                            )}
                            {pageState.categories && (
                                <>
                                    <Row>
                                        {pageState.hasTags &&
                                            <>
                                                <Col xs={4} sm={8} md={2} lg={2} xl={2} style={{
                                                    borderRight: "1px solid #D7D7DE",
                                                    wordBreak: "break-word",
                                                }}
                                                >
                                                    <FilterSection
                                                        tags={pageState.tags}
                                                        handleChange={handleChange}
                                                    />
                                                </Col>
                                            </>
                                        }
                                        <Col
                                            xs={pageState.hasTags ? 4 : 12}
                                            sm={pageState.hasTags ? 8 : 12}
                                            md={pageState.hasTags ? 6 : 12}
                                            lg={pageState.hasTags ? 10 : 12}
                                            xl={pageState.hasTags ? 10 : 12}>
                                            <CategoryListSection
                                                data={pageState.categories.filter(
                                                    (c) => c.libraryPosts && c.libraryPosts.length > 0
                                                )}
                                                displayPostId={user && user.entitleId === UserRole.SuperAdmin_Entitlement}
                                                defaultExpanded={pageState.defaultExpanded}
                                                handleClickLinkBtn={handleClickLinkBtn}
                                            />
                                        </Col>
                                        {confirmDialog && (
                                            <MCSConfirmDialog
                                                confirmHandler={LibraryHelper.handleLibraryPostOnClick}
                                                confirmDialog={confirmDialog}
                                                setConfirmDialog={setConfirmDialog}
                                            />
                                        )}
                                    </Row>
                                </>
                            )}
                        </MCSGridRevert>
                    </>
                )}
            </GlobalAlertContext.Consumer>
        );
    }
}

export default Library;
